import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { VideoPlayerProps, VideoPlayerMethods } from './types';

/**
 * Storage Video Player Component
 * HTML5 video player for cloudflare_r2, supabase_storage, and local video sources
 */
const StorageVideoPlayer = forwardRef<VideoPlayerMethods, VideoPlayerProps>(({
  videoUrl,
  onTimeUpdate,
  onStateChange,
  onReady,
  onError,
  autoplay = false,
  startTime,
  className = '',
  width = '100%',
  height = '100%',
  controls = true,
}, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timeUpdateIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Store props in refs to access latest values without causing re-initializations
  const propsRef = useRef({ autoplay, startTime, controls, width, height, onReady, onStateChange, onError });
  useEffect(() => {
    propsRef.current = { autoplay, startTime, controls, width, height, onReady, onStateChange, onError };
  }, [autoplay, startTime, controls, width, height, onReady, onStateChange, onError]);

  // Expose player methods through ref
  useImperativeHandle(ref, () => ({
    play: async () => {
      try {
        const video = videoRef.current;
        if (!video) return;
        
        // Check if video is already playing to avoid conflicts
        if (!video.paused) {
          return; // Already playing, no need to call play()
        }
        
        // play() returns a Promise - handle it properly to avoid interruption errors
        const playPromise = video.play();
        if (playPromise !== undefined) {
          // Handle the promise to catch interruption errors gracefully
          await playPromise.catch((error: unknown) => {
            // Ignore AbortError and interruption errors - they're harmless
            // This happens when pause() is called before play() promise resolves
            if (error instanceof Error) {
              if (error.name !== 'AbortError' && !error.message?.includes('interrupted')) {
                console.warn('Video play error:', error);
              }
            } else {
              // For non-Error types, log if it's not a string containing 'interrupted'
              const errorStr = String(error);
              if (!errorStr.includes('interrupted')) {
                console.warn('Video play error:', error);
              }
            }
          });
        }
      } catch (error) {
        // Ignore AbortError - happens when play() is interrupted by pause()
        // Type guard to check if error is an Error instance
        if (error instanceof Error) {
          if (error.name !== 'AbortError' && !error.message?.includes('interrupted')) {
            console.warn('Error playing video:', error);
          }
        } else {
          // For non-Error types, log if it's not a string containing 'interrupted'
          const errorStr = String(error);
          if (!errorStr.includes('interrupted')) {
            console.warn('Error playing video:', error);
          }
        }
      }
    },
    pause: () => {
      try {
        videoRef.current?.pause();
      } catch (error) {
        console.warn('Error pausing video:', error);
      }
    },
    seekTo: (seconds: number) => {
      try {
        if (videoRef.current) {
          videoRef.current.currentTime = seconds;
        }
      } catch (error) {
        console.warn('Error seeking video:', error);
      }
    },
    getCurrentTime: () => {
      try {
        return videoRef.current?.currentTime || 0;
      } catch (error) {
        console.warn('Error getting current time:', error);
        return 0;
      }
    },
    getDuration: () => {
      try {
        return videoRef.current?.duration || 0;
      } catch (error) {
        console.warn('Error getting duration:', error);
        return 0;
      }
    },
    setVolume: (volume: number) => {
      try {
        if (videoRef.current) {
          // HTML5 volume is 0-1, but interface expects 0-100
          videoRef.current.volume = Math.max(0, Math.min(1, volume / 100));
        }
      } catch (error) {
        console.warn('Error setting volume:', error);
      }
    },
    getVolume: () => {
      try {
        // Convert from 0-1 to 0-100
        return (videoRef.current?.volume || 0) * 100;
      } catch (error) {
        console.warn('Error getting volume:', error);
        return 0;
      }
    },
    setPlaybackRate: (rate: number) => {
      try {
        if (videoRef.current) {
          videoRef.current.playbackRate = rate;
        }
      } catch (error) {
        console.warn('Error setting playback rate:', error);
      }
    },
    getPlaybackRate: () => {
      try {
        return videoRef.current?.playbackRate || 1;
      } catch (error) {
        console.warn('Error getting playback rate:', error);
        return 1;
      }
    },
    mute: () => {
      try {
        if (videoRef.current) {
          videoRef.current.muted = true;
        }
      } catch (error) {
        console.warn('Error muting video:', error);
      }
    },
    unMute: () => {
      try {
        if (videoRef.current) {
          videoRef.current.muted = false;
        }
      } catch (error) {
        console.warn('Error unmuting video:', error);
      }
    },
    isMuted: () => {
      try {
        return videoRef.current?.muted || false;
      } catch (error) {
        console.warn('Error checking mute status:', error);
        return false;
      }
    },
  }));

  // Time update polling (similar to YouTubePlayer)
  const startTimeUpdatePolling = useCallback((): void => {
    stopTimeUpdatePolling(); // Clear any existing interval

    timeUpdateIntervalRef.current = setInterval(() => {
      try {
        const currentTime = videoRef.current?.currentTime;
        if (currentTime !== undefined && currentTime !== null && !isNaN(currentTime)) {
          onTimeUpdate?.(currentTime);
        }
      } catch (error) {
        console.warn('Error getting current time:', error);
      }
    }, 100); // Update every 100ms for smooth time tracking
  }, [onTimeUpdate]);

  const stopTimeUpdatePolling = useCallback(() => {
    if (timeUpdateIntervalRef.current) {
      clearInterval(timeUpdateIntervalRef.current);
      timeUpdateIntervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopTimeUpdatePolling();
    };
  }, [stopTimeUpdatePolling]);

  // Handle video URL changes
  useEffect(() => {
    if (!videoRef.current || !videoUrl) {
      return;
    }

    setIsLoading(true);
    setError(null);
    videoRef.current.src = videoUrl;
    videoRef.current.load();
  }, [videoUrl]);

  // Handle video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedMetadata = () => {
      setIsLoading(false);
      const currentProps = propsRef.current;
      
      // Seek to start time if specified
      if (currentProps.startTime !== undefined && currentProps.startTime > 0) {
        video.currentTime = currentProps.startTime;
      }

      // Call onReady callback
      currentProps.onReady?.();
    };

    const handleCanPlay = () => {
      setIsLoading(false);
    };

    const handlePlay = () => {
      startTimeUpdatePolling();
      const currentProps = propsRef.current;
      currentProps.onStateChange?.('playing');
    };

    const handlePause = () => {
      stopTimeUpdatePolling();
      const currentProps = propsRef.current;
      currentProps.onStateChange?.('paused');
    };

    const handleEnded = () => {
      stopTimeUpdatePolling();
      const currentProps = propsRef.current;
      currentProps.onStateChange?.('ended');
    };

    const handleError = () => {
      setIsLoading(false);
      const errorMessage = video.error 
        ? `Video error: ${video.error.code} - ${video.error.message || 'Unknown error'}`
        : 'Failed to load video';
      setError(errorMessage);
      const currentProps = propsRef.current;
      const err = new Error(errorMessage);
      currentProps.onError?.(err);
    };

    const handleWaiting = () => {
      // Video is buffering
      const currentProps = propsRef.current;
      currentProps.onStateChange?.('buffering' as any);
    };

    const handlePlaying = () => {
      // Video started playing after buffering
      startTimeUpdatePolling();
      const currentProps = propsRef.current;
      currentProps.onStateChange?.('playing');
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    video.addEventListener('waiting', handleWaiting);
    video.addEventListener('playing', handlePlaying);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      video.removeEventListener('waiting', handleWaiting);
      video.removeEventListener('playing', handlePlaying);
    };
  }, [startTimeUpdatePolling, stopTimeUpdatePolling]);

  // Handle keyboard shortcuts for skip forward/backward (5 seconds)
  // Using capture phase to intercept before native controls
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle arrow keys
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      
      // Stop the event completely - prevent native browser skip behavior
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      
      const skipAmount = 5; // 5 seconds like YouTube
      const currentTime = video.currentTime;
      const duration = video.duration || 0;
      
      if (e.key === 'ArrowLeft') {
        // Skip backward
        video.currentTime = Math.max(0, currentTime - skipAmount);
      } else if (e.key === 'ArrowRight') {
        // Skip forward
        video.currentTime = Math.min(duration, currentTime + skipAmount);
      }
    };

    // Use capture phase to intercept before native video controls handle it
    video.addEventListener('keydown', handleKeyDown, true);
    
    return () => {
      video.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  // Handle autoplay
  useEffect(() => {
    if (autoplay && videoRef.current && !isLoading && !error) {
      videoRef.current.play().catch((err) => {
        console.warn('Autoplay prevented:', err);
      });
    }
  }, [autoplay, isLoading, error]);

  if (!videoUrl) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className}`} style={{ width, height }}>
        <p className="text-gray-500 text-sm">No video URL provided</p>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} style={{ width, height }}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-gray-500 text-sm">Loading video...</div>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50 z-10">
          <div className="text-red-600 text-sm text-center px-4">
            <p className="font-medium">Error loading video</p>
            <p className="text-xs mt-1">{error}</p>
          </div>
        </div>
      )}
      <video
        ref={videoRef}
        src={videoUrl}
        controls={controls}
        className="w-full h-full"
        preload="metadata"
        playsInline
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
});

StorageVideoPlayer.displayName = 'StorageVideoPlayer';

export default StorageVideoPlayer;
